import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { allVideos, Video } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [activeTag, setActiveTag] = useState<string>('全部');

  const category = categories.find((c) => c.slug === slug);

  const categoryVideos = useMemo(() => {
    return allVideos.filter((v) => v.categorySlug === slug);
  }, [slug]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    categoryVideos.forEach((v) => v.tags.forEach((t) => tagSet.add(t)));
    return ['全部', ...Array.from(tagSet)];
  }, [categoryVideos]);

  const filteredVideos = useMemo(() => {
    let videos = categoryVideos;
    if (activeTag !== '全部') {
      videos = videos.filter((v) => v.tags.includes(activeTag));
    }
    return [...videos].sort((a, b) => {
      if (sortBy === 'popular') return b.views - a.views;
      if (sortBy === 'rating') return b.rating - a.rating;
      return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
    });
  }, [categoryVideos, activeTag, sortBy]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 pb-10 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您访问的分类不存在</p>
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://moguvideo.cn/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://moguvideo.cn/category/${slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="CollectionPage" data={{ name: `蘑菇视频${category.name}分类 - 海量高清视频免费看`, description: category.description, url: `https://moguvideo.cn/category/${slug}` }} />
      <Navbar />
      <main className="min-h-screen pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              蘑菇视频{category.name}分类
            </h1>
            <p className="text-sm text-foreground-500">{category.description} · 共 {categoryVideos.length} 个视频</p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 flex-wrap">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTag === tag
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序：</span>
              <div className="flex bg-background-100 rounded-full p-0.5">
                {([
                  { key: 'latest', label: '最新' },
                  { key: 'popular', label: '最热' },
                  { key: 'rating', label: '评分' },
                ] as const).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {filteredVideos.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-video-line text-3xl"></i>
              </div>
              <p className="text-sm text-foreground-500">该标签下暂无视频</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6" data-product-shop>
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            <div className="bg-background-100/70 rounded-lg p-5">
              <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-3">
                <a href="#other-categories" className="hover:text-primary-600 transition-colors">浏览其他分类</a>
              </h4>
              <div className="flex flex-wrap gap-2">
                {categories.filter(c => c.slug !== slug).slice(0, 6).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-200/70 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap"
                  >
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`${cat.icon} text-[10px]`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}